import React, { useState } from 'react'
import { AdminLayout } from '../components/AdminLayout'
import { adminApi } from '../services/adminApi'
import {
  Upload,
  AlertCircle,
  CheckCircle,
  FileUp,
  Wrench
} from 'lucide-react'

export const UtilitiesPage = () => {
  const [file, setFile] = useState(null)
  const [metadataFile, setMetadataFile] = useState(null)
  const [modelGraphFile, setModelGraphFile] = useState(null)
  const [constMetaFile, setConstMetaFile] = useState(null)
  const [modelName, setModelName] = useState('')
  const [modelArchitecture, setModelArchitecture] = useState('resnet50')
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [dragActive, setDragActive] = useState(false)

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0]
      const supportedTypes = ['.py', '.pth', '.pt', '.pkl']
      if (supportedTypes.some((ext) => droppedFile.name.toLowerCase().endsWith(ext))) {
        setFile(droppedFile)
        if (!modelName) {
          setModelName(droppedFile.name.replace(/\.(pth|pt|pkl|py)$/i, ''))
        }
      } else {
        setError('Please upload a supported file type (.py, .pth, .pt, .pkl)')
      }
    }
  }

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0]
    if (selectedFile) {
      setFile(selectedFile)
      if (!modelName) {
        setModelName(selectedFile.name.replace(/\.(pth|pt|pkl|py)$/i, ''))
      }
      setError('')
    }
  }

  const handleUpload = async (e) => {
    e.preventDefault()

    if (!file || !modelName.trim()) {
      setError('Please select a model file and enter a model name')
      return
    }

    if (!metadataFile) {
      setError('Please select a metadata JSON file (tensor_io_log.json)')
      return
    }

    if (!modelGraphFile) {
      setError('Please select a model graph JSON file')
      return
    }

    setUploading(true)
    setError('')
    setSuccess('')

    try {
      const result = await adminApi.uploadModel({
        file,
        modelName: modelName.trim(),
        modelArchitecture,
        metadataFile,
        modelGraphFile,
        constMetaFile
      })

      setSuccess(`Model "${modelName}" uploaded successfully! Model ID: ${result.model_id}`)

      // Reset form
      setFile(null)
      setMetadataFile(null)
      setModelGraphFile(null)
      setConstMetaFile(null)
      setModelName('')
      setModelArchitecture('resnet50')
    } catch (err) {
      setError(err.message)
    } finally {
      setUploading(false)
    }
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Wrench className="h-8 w-8 text-gray-700" />
            <h1 className="text-3xl font-bold text-gray-900">Utilities</h1>
          </div>
          <p className="text-gray-500">Administrative tools and utilities</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Upload PyTorch Model</h2>

          {success && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-green-800">Success</p>
                <p className="text-sm text-green-700 mt-1">{success}</p>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Error</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleUpload} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Model Name *
              </label>
              <input
                type="text"
                value={modelName}
                onChange={(e) => setModelName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., My ResNet50, Custom CNN"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Model Architecture *
              </label>
              <select
                value={modelArchitecture}
                onChange={(e) => setModelArchitecture(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="resnet50">ResNet-50</option>
                <option value="yolop">YOLOP</option>
                <option value="efficientnet_b0">EfficientNet-B0</option>
                <option value="vit_base">Vision Transformer (ViT-Base)</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Select the base architecture of your model for correct weight loading
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <p className="text-sm text-blue-700">
                <strong>Hardware-agnostic upload:</strong> Your model will be uploaded without hardware assignment.
                After upload, select hardware targets to create optimized variants.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Model File (Python) *
                </label>
                <div
                  className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    dragActive
                      ? 'border-blue-500 bg-blue-50'
                      : file
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    accept=".py,.pth,.pt,.pkl"
                    onChange={handleFileSelect}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />

                  {file ? (
                    <div className="space-y-2">
                      <CheckCircle className="h-8 w-8 text-green-600 mx-auto" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{file.name}</p>
                        <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFile(null)}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        Remove file
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <FileUp className={`h-8 w-8 mx-auto ${dragActive ? 'text-blue-600' : 'text-gray-400'}`} />
                      <div>
                        <p className="text-sm text-gray-600">
                          Drop your model Python file here, or{' '}
                          <span className="text-blue-600 font-medium">browse</span>
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Clustered graph with COMPOSITE functions (.py)
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  I/O Metadata (JSON) *
                </label>
                <div
                  className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    metadataFile
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <input
                    type="file"
                    accept=".json"
                    onChange={(e) => {
                      const selectedFile = e.target.files[0]
                      if (selectedFile) {
                        setMetadataFile(selectedFile)
                        setError('')
                      }
                    }}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />

                  {metadataFile ? (
                    <div className="space-y-2">
                      <CheckCircle className="h-8 w-8 text-green-600 mx-auto" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{metadataFile.name}</p>
                        <p className="text-xs text-gray-500">{formatFileSize(metadataFile.size)}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setMetadataFile(null)}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        Remove file
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <FileUp className="h-8 w-8 mx-auto text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-600">
                          Drop tensor_io_log.json here, or{' '}
                          <span className="text-blue-600 font-medium">browse</span>
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          JSON file with input/output shapes
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Model Graph (JSON) *
              </label>
              <div
                className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  modelGraphFile
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <input
                  type="file"
                  accept=".json"
                  onChange={(e) => {
                    const selectedFile = e.target.files[0]
                    if (selectedFile) {
                      setModelGraphFile(selectedFile)
                      setError('')
                    }
                  }}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />

                {modelGraphFile ? (
                  <div className="space-y-2">
                    <CheckCircle className="h-8 w-8 text-green-600 mx-auto" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{modelGraphFile.name}</p>
                      <p className="text-xs text-gray-500">{formatFileSize(modelGraphFile.size)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setModelGraphFile(null)}
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      Remove file
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <FileUp className="h-8 w-8 mx-auto text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-600">
                        Drop model graph JSON here, or{' '}
                        <span className="text-blue-600 font-medium">browse</span>
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        JSON file with model execution graph structure
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Parameter Metadata (const_meta.json){' '}
                <span className="text-gray-400 text-xs">- Optional</span>
              </label>
              <div
                className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  constMetaFile
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <input
                  type="file"
                  accept=".json"
                  onChange={(e) => {
                    const selectedFile = e.target.files[0]
                    if (selectedFile) {
                      setConstMetaFile(selectedFile)
                      setError('')
                    }
                  }}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />

                {constMetaFile ? (
                  <div className="space-y-2">
                    <CheckCircle className="h-8 w-8 text-green-600 mx-auto" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{constMetaFile.name}</p>
                      <p className="text-xs text-gray-500">{formatFileSize(constMetaFile.size)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setConstMetaFile(null)}
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      Remove file
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <FileUp className="h-8 w-8 mx-auto text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-600">
                        Drop const_meta.json here, or{' '}
                        <span className="text-blue-600 font-medium">browse</span>
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Parameter metadata for IO regeneration (shapes, dtypes)
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={uploading || !file || !metadataFile || !modelGraphFile || !modelName.trim()}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 transition-colors"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Processing Model...</span>
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  <span>Upload Model</span>
                </>
              )}
            </button>

            <div className="text-xs text-gray-500 space-y-1">
              <p>• Python file, I/O metadata JSON, and model graph JSON are required</p>
              <p>• Parameter metadata (const_meta.json) is optional but required for IO regeneration</p>
              <p>• Python file should contain COMPOSITE functions</p>
              <p>• Models are uploaded hardware-agnostic - no hardware assignment required</p>
              <p>• Processing may take a few seconds depending on model size</p>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  )
}
